import { Component, inject, signal, computed } from '@angular/core';
import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { CommonModule, NgClass } from '@angular/common';
import { ConfirmationService, MessageService, PrimeTemplate } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ApplicationPayload, ApplicationService } from '../../../service/application.service';
import { Button, ButtonDirective } from 'primeng/button';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Application } from '../../models/application.model';
import { Dialog } from 'primeng/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { Toast } from 'primeng/toast';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

@Component({
  selector: 'app-application',
  templateUrl: './application.component.html',
  imports: [
    PageLayoutComponent,
    PrimeTemplate,
    TableModule,
    NgClass,
    Button,
    Dialog,
    ReactiveFormsModule,
    InputTextModule,
    ButtonDirective,
    Toast,
    StateContainerComponent,
    CommonModule
  ],
  providers: [MessageService, ConfirmationService],
})
export class ApplicationComponent {
  private applicationService = inject(ApplicationService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  applications = signal<Application[]>([]);
  loading = signal(false);
  error = signal<any>(null);

  addApplicationForm!: FormGroup<{
    name: FormControl<string>;
    clientId: FormControl<string>;
    code: FormControl<string>;
    defaultRedirectUri: FormControl<string>;
    iconUrl: FormControl<string>;
  }>;
  showAddApplicationDialog = false;
  isEditMode = false;
  viewMode = false;
  selectedApplicationId: number | null = null;

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.applications().length === 0) return 'empty';
    return 'content';
  });

  readonly emptyStateMessage = computed(() => {
    return 'Create your first application to start managing your applications.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load applications. Please try again.';
  });

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.initForm();
    this.loadApplications();
  }

  initForm() {
    this.addApplicationForm = this.fb.group({
      name: this.fb.control('', { nonNullable: true, validators: [Validators.required] }),
      clientId: this.fb.control('', { nonNullable: true }),
      code: this.fb.control('', { nonNullable: true, validators: [Validators.required] }),
      defaultRedirectUri: this.fb.control('', { nonNullable: true }),
      iconUrl: this.fb.control('', { nonNullable: true })
    });
  }

  loadApplications() {
    this.loading.set(true);
    this.error.set(null);
    this.applicationService.getApplications().subscribe({
      next: (res: any) => {
        this.applications.set(res.content || res);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
      }
    });
  }

  // Retry method for error state
  retryLoad() {
    this.loadApplications();
  }

  // View application details
  viewApplicationDetails(application: Application) {
    this.viewMode = true;
    this.isEditMode = false;
    this.selectedApplicationId = null;

    // Disable the form when viewing
    this.addApplicationForm.disable();

    this.addApplicationForm.setValue({
      name: application.name || '',
      clientId: String(application.id) || application.clientId || '',
      code: application.code || '',
      defaultRedirectUri: application.defaultRedirectUri || '',
      iconUrl: application.iconUrl || ''
    });

    this.showAddApplicationDialog = true;
  }

  createOrUpdateApplication() {
    if (this.addApplicationForm.invalid) {
      this.addApplicationForm.markAllAsTouched();
      return;
    }
    const payload: ApplicationPayload = this.addApplicationForm.getRawValue();
    if (this.isEditMode && this.selectedApplicationId) {
      this.applicationService
        .updateApplication(this.selectedApplicationId, payload)
        .subscribe({
          next: () => {
            this.afterSave();
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Application updated successfully',
            });
          },
          error: (err) => {
            console.error(err);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update application',
            });
          }
        });
    } else {
      this.applicationService.createApplication(payload).subscribe({
        next: () => {
          this.afterSave();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Application created successfully',
          });
        },
        error: (err) => {
          console.error(err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create application',
          });
        }
      });
    }
  }

  afterSave() {
    this.showAddApplicationDialog = false;
    this.isEditMode = false;
    this.viewMode = false;
    this.selectedApplicationId = null;
    // Re-enable the form
    this.addApplicationForm.enable();
    this.loadApplications();
  }

  openAddApplicationDialog() {
    this.viewMode = false;
    this.isEditMode = false;
    this.selectedApplicationId = null;

    // Re-enable the form for creating
    this.addApplicationForm.enable();

    this.addApplicationForm.reset({
      name: '',
      clientId: '',
      code: '',
      defaultRedirectUri: '',
      iconUrl: ''
    });

    this.showAddApplicationDialog = true;
  }

  editApplication(application: Application) {
    this.viewMode = false;
    this.isEditMode = true;
    this.selectedApplicationId = application.id;

    // Re-enable the form for editing
    this.addApplicationForm.enable();

    this.addApplicationForm.setValue({
      name: application.name || '',
      clientId: application.clientId || '',
      code: application.code || '',
      defaultRedirectUri: application.defaultRedirectUri || '',
      iconUrl: application.iconUrl || ''
    });

    this.showAddApplicationDialog = true;
  }

  deleteApplication(application: Application) {
    this.selectedApplicationId = application.id;

    this.applicationService.deleteApplication(application.id).subscribe({
      next: () => {
        this.selectedApplicationId = null;
        this.loadApplications();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Application deleted successfully',
        });
      },
      error: (err) => {
        console.error(err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete application',
        });
      }
    });
  }

  closeDialog() {
    this.showAddApplicationDialog = false;
    this.viewMode = false;
    this.isEditMode = false;
    this.selectedApplicationId = null;
    // Re-enable the form
    this.addApplicationForm.enable();
    this.addApplicationForm.reset({
      name: '',
      clientId: '',
      code: '',
      defaultRedirectUri: '',
      iconUrl: ''
    });
  }
}